'use client';
import React, { useState } from 'react';
import { TasticProps } from '../types';
import { Props } from './types';
import ProjectBuilderProductListAlgoliaTastic from '@/lib/tastics/project-builder-page/products/project-builder-product-list-algolia';
import ViewToggleButton from '../project-builder-page/components/toggleView';
import { localeMapping } from '@/constants/localeMapping';
import {
  getFacetsConfiguration,
  isBrandListingSlug,
  normalizeSlugSegment,
  resolveCategorySlugFromPathSegments,
} from './categoryFacetsConfiguration';
import {
  buildSubCategoryLvl1Filter,
  normalizeCategorySlug
} from '@/constants/category-pages';

const ProductListTastic = ({
  searchParams,
  flatCategories = [],
  treeCategories = [],
  params,
}: TasticProps<Props>) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const toggleView = () => {
    setViewMode((prevMode) => (prevMode === 'grid' ? 'list' : 'grid'));
  };
  const categories = treeCategories.length > 0 ? treeCategories : flatCategories;

  const locale = (params as any)?.locale as string | undefined;
  const slugSegments = (params as any)?.slug as string[] | undefined;
  const categorySlugFromUrl = slugSegments && slugSegments.length > 0 ? slugSegments[slugSegments.length - 1] : undefined;

  const localeKey = locale ? localeMapping[locale] : undefined;
  const categorySlugs = [
    'sanitaryware',
    'tapware',
    'showers',
    'basins',
    'kitchen-sinks-accessories',
    'laundry-tubs',
    'spare-parts',
    'baths',
    'valves-fittings',
    'bathroom-accessories',
    "plumber's-favourites"
  ];

  const subcategorySlugs = [
    'bath-spare-parts',
    'basin-spare-parts',
    'shower-spare-parts',
    'tapware-spare-parts',
    'toilet-spare-parts',
    'valve-spare-parts',
    'kitchen-laundry-spare-parts',
  ];

  let defaultFilters: string | undefined;

  const normalizedSlug = categorySlugFromUrl ? normalizeCategorySlug(categorySlugFromUrl) : undefined;
  const subCategoryParam = searchParams?.subCategory
    ? normalizeCategorySlug(searchParams.subCategory)
    : undefined;

  if (localeKey && normalizedSlug) {
    if (normalizedSlug === 'see-all') {
      defaultFilters = undefined;
    }
    else if (normalizedSlug === 'plumbers-favourites') {
      defaultFilters =
        `searchableAttributes.plumber-bundle:"${locale}"`;
    }
    else if (categorySlugs.includes(normalizedSlug)) {
      defaultFilters = `categories.${localeKey}.lvl0:"${normalizedSlug.replace(/-/g, ' ')}"`;
       if (subCategoryParam) {
        defaultFilters = `${defaultFilters} AND ${buildSubCategoryLvl1Filter(normalizedSlug, subCategoryParam, localeKey)}`;
      }
    }
    else if (subcategorySlugs.includes(normalizedSlug)) {
      const toTitleCase = (str: string) =>
        str
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());

      const child = toTitleCase(normalizedSlug);

      defaultFilters = `categories.${localeKey}.lvl1:"Spare Parts > ${child}"`;
    }
    else if(normalizedSlug === 'leak-smartshield') {
      defaultFilters = `searchableAttributes.brand:'Leak SmartShield®' OR searchableAttributes.brand:'Leak SmartShield™'`;
    }
    else if (normalizedSlug === 'tradeready') {
      defaultFilters = `searchableAttributes.range:"TradeReady"`;
    }
    else if (isBrandListingSlug(normalizedSlug)) {
      defaultFilters = `searchableAttributes.brand:"${normalizedSlug}"`;
    }
  }

  const categorySlugForFacets =
    resolveCategorySlugFromPathSegments(slugSegments ?? []) ?? normalizedSlug;

  const facetsConfiguration = getFacetsConfiguration(categorySlugForFacets, locale);

  const data = {
    mobile: true,
    tablet: true,
    desktop: true,
    facetsConfiguration,
    pricesConfiguration: [],
  };

  return (
    <div className="mt-5 py-8 px-5 lg:px-10 bg-[#F3EDE9]">
      <ProjectBuilderProductListAlgoliaTastic
        data={data}
        params={params}
        searchParams={searchParams}
        categories={categories}
        viewMode={viewMode}
        toggleView={toggleView}
        defaultFilters={defaultFilters}
      />
    </div>
  );
};

export default ProductListTastic;